// E-sign fields (X18-E01): the fields a signer fills inside an agreement. A field belongs to one signing order (or to every
// signer when `assignee` is 0), sits after one block of the document, and may carry a default, a lock and a validation rule.
// The checks that need the document or the signers (options present, one signature per signer, a real placement) live in
// mod-money/src/esign-fields.ts.
import { z } from "zod";

export const ESIGN_FIELD_KIND = z.enum(["signature", "initials", "date", "text", "number", "checkbox", "radio", "dropdown"]);
export const ESIGN_FIELD_FORMAT = z.enum(["email", "phone", "zip", "date", "number", "regex"]);

/** A check on what a signer types. `format` is a named shape; `pattern` is a regular expression (at most 200 characters);
 *  `min` and `max` bound a number's value, or a text's length in characters. */
export const ESIGN_VALIDATION = z.strictObject({
  format: ESIGN_FIELD_FORMAT.optional(),
  pattern: z.string().max(200).optional(),
  min: z.number().finite().optional(),
  max: z.number().finite().optional(),
  message: z.string().trim().max(200).optional(),
});

export const ESIGN_FIELD = z.strictObject({
  id: z.string().min(1).max(60),
  kind: ESIGN_FIELD_KIND,
  label: z.string().trim().min(1).max(120),
  /** The signing order this field is for; 0 means every signer fills it in. */
  assignee: z.number().int().min(0).max(50),
  required: z.boolean(),
  /** The block of the document the field sits after. Unset: at the end. */
  afterBlockId: z.string().min(1).max(100).optional(),
  /** The choices of a radio or dropdown field. */
  options: z.array(z.string().trim().min(1).max(120)).max(20).optional(),
  /** The value the field starts with. A dropdown or radio default is one of its options. */
  default: z.string().max(500).optional(),
  /** A locked field keeps its default; the signer cannot change it. */
  locked: z.boolean().optional(),
  validation: ESIGN_VALIDATION.optional(),
});
export type EsignField = z.infer<typeof ESIGN_FIELD>;
export type EsignValidation = z.infer<typeof ESIGN_VALIDATION>;

/** The answers a signer gives: the value per field id. A signature or initials value is a signature image (PNG or JPG data URL). */
export const ESIGN_RESPONSES = z.record(z.string().min(1).max(60), z.string().max(300_000));
